"use client";

import { useQuery } from "@tanstack/react-query";
import { useContext, useState, useEffect } from "react";
import { TextField, Autocomplete } from "@mui/material";
import { FilterParamsContext } from "../FilterParamsProvider";
import { useOrganizationQuery } from "@/hooks/organizations";
import { organizationsMapOptions } from "@/queryOptions/organizations";

export default function ChannelSelect() {
  const { filterParams, setFilterParams } = useContext(FilterParamsContext);
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");

  // Debounce the input value
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [inputValue]);

  // Use react-query to fetch organizations with debounced input
  const {
    data: options = [],
    isLoading: loading,
    isFetched,
  } = useOrganizationQuery(
    debouncedInputValue,
    filterParams.states?.map(String),
    filterParams.counties?.map(String),
    filterParams.cities?.map(String),
    filterParams.channel_types?.map(String),
  );

  const filteredOptions = options
    .filter((organization) => {
      let shouldInclude = true;
      if (filterParams.states?.length) {
        shouldInclude = filterParams.states.includes(
          organization.state?.id ?? -1,
        );
      }
      if (filterParams.counties?.length) {
        shouldInclude =
          shouldInclude &&
          filterParams.counties.includes(organization.county?.id ?? -1);
      }
      if (filterParams.cities?.length) {
        shouldInclude =
          shouldInclude &&
          filterParams.cities.includes(organization.city?.id ?? -1);
      }
      if (filterParams.channel_types?.length) {
        shouldInclude =
          shouldInclude &&
          filterParams.channel_types.includes(organization.type?.id ?? -1);
      }
      return shouldInclude;
    })
    .sort(
      (organizationA, organizationB) =>
        organizationA.state?.name.localeCompare(
          organizationB.state?.name ?? "",
        ) ?? 0,
    );

  const optionMap = new Map(filteredOptions.map((org) => [org.id, org]));

  // Create a map for quick lookup of organizations by ID
  const { data: organizationMap } = useQuery(organizationsMapOptions());

  return (
    <Autocomplete
      multiple
      options={filteredOptions.map((org) => org.id)}
      groupBy={(option) => optionMap.get(option)?.state?.name || ""}
      value={filterParams.organizations}
      onChange={(_event, newValue) => {
        setFilterParams({
          ...filterParams,
          organizations: newValue,
        });
      }}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Channel"
          placeholder="Search for channels..."
        />
      )}
      getOptionLabel={(option) => organizationMap?.get(option)?.name || "..."}
      getOptionKey={(option) => option}
      loading={loading}
      disableCloseOnSelect
      noOptionsText={
        filteredOptions.length > 0 || !isFetched
          ? "Type to search"
          : "No options found"
      }
      filterOptions={(x) => x} // Disable built-in filtering since we're using API search
    />
  );
}
